import { OverlayContainer } from '@angular/cdk/overlay';
import {
    Component,
    DestroyRef,
    OnInit,
    ViewEncapsulation,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { InconcertSharedService } from 'app/shared/service/inconcertShared.service';
import { filter } from 'rxjs';
import { IcLoadingBarComponent } from 'app/shared/components/others/loading-bar';
import { IcNavigationComponent, UiNavigationItem } from 'app/shared/components/others/navigation';
import { QaNodesDirective, qaFor } from '@ui/qa';
import { IcAppFrameComponent } from './_app-frame/_app-frame.component';

const REFACTOR_OVERLAY_CLASS = 'ic-refactor-overlay';

const NAVIGATION_QA = new Map<string, UiNavigationItem['qa']>([
    ['my-org', { link: 'navigation.home.open' }],
    ['teams', { link: 'navigation.crews.open' }],
    ['agents-and-teams', { link: 'navigation.crews.open' }],
    ['kbs', { link: 'navigation.knowledge.open' }],
    ['trash', { link: 'navigation.recycle-bin.open' }],
    ['configuration', { link: 'navigation.configuration.open' }],
]);

function withNavigationQa(item: UiNavigationItem): UiNavigationItem {
    const qa = item.id ? NAVIGATION_QA.get(item.id) : undefined;
    const children = item.children?.map(withNavigationQa);
    return { ...item, ...(qa ? { qa } : {}), ...(children ? { children } : {}) };
}

@Component({
    selector: 'ic-layout',
    standalone: true,
    templateUrl: './ic-layout.component.html',
    styleUrl: './ic-layout.component.scss',
    encapsulation: ViewEncapsulation.None,
    host: { class: 'ic-layout' },
    imports: [
        IcLoadingBarComponent,
        IcNavigationComponent,
        IcAppFrameComponent,
        RouterOutlet,
        QaNodesDirective,
    ],
})
export class IcLayoutComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly navigationService = inject(NavigationService);
    private readonly fuseMediaWatcherService = inject(FuseMediaWatcherService);
    private readonly fuseNavigationService = inject(FuseNavigationService);
    private readonly inconcertSharedService = inject(InconcertSharedService);
    private readonly overlayContainer = inject(OverlayContainer);

    readonly isScreenSmall = signal(false);
    readonly navigation = signal<Navigation>(undefined as unknown as Navigation);
    readonly navigationAppearance = signal<'default' | 'dense'>('dense');
    readonly isLegacyRoute = signal(false);

    protected readonly menuQa = qaFor(IcNavigationComponent, { section: 'navigation.sections.item' });
    protected readonly menu = computed(() =>
        this.navigation()?.default?.map(withNavigationQa),
    );

    ngOnInit(): void {
        this.navigationService.navigation$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((navigation: Navigation) => {
                this.navigation.set(navigation);
            });

        this.fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ matchingAliases }) => {
                const wasSmall = this.isScreenSmall();
                const nowSmall = !matchingAliases.includes('md');
                this.isScreenSmall.set(nowSmall);

                if (wasSmall && !nowSmall) {
                    this.navigationAppearance.set('dense');
                } else if (!wasSmall && nowSmall) {
                    this.navigationAppearance.set('default');
                }
            });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.resolveLegacyFlag();

                const nav = this.fuseNavigationService
                    .getComponent<FuseVerticalNavigationComponent>('mainNavigation');
                if (nav?.mode === 'over' && nav.opened) {
                    nav.close();
                }
            });

        this.resolveLegacyFlag();

        if (this.inconcertSharedService.getMenuOpen()) {
            this.toggleNavigationAppearance();
            this.inconcertSharedService.setMenuOpen(false);
        }
    }

    topBarToggle(name: string): void {
        if (this.isScreenSmall()) {
            this.toggleNavigation(name);
            return;
        }
        this.toggleNavigationAppearance();
    }

    private toggleNavigation(name: string): void {
        const nav = this.fuseNavigationService
            .getComponent<FuseVerticalNavigationComponent>(name);
        nav?.toggle();
    }

    private toggleNavigationAppearance(): void {
        this.navigationAppearance.set(
            this.navigationAppearance() === 'default' ? 'dense' : 'default',
        );
    }

    private resolveLegacyFlag(): void {
        let route = this.activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        let isLegacy = false;
        route.pathFromRoot.forEach((path) => {
            const data = path.routeConfig?.data;
            if (data && Object.prototype.hasOwnProperty.call(data, 'isLegacy')) {
                isLegacy = !!data['isLegacy'];
            }
        });

        this.isLegacyRoute.set(isLegacy);
        this.syncOverlayScope(isLegacy);
    }

    private syncOverlayScope(isLegacy: boolean): void {
        this.overlayContainer
            .getContainerElement()
            .classList.toggle(REFACTOR_OVERLAY_CLASS, !isLegacy);
    }
}
